import React, { useEffect, useState } from "react";
import styled from "styled-components";
import LineBox from "../../components/DefaultStyles/LineBox";
import PageTitle from "../../components/DefaultStyles/PageTitle";
import Pagination from "../../components/Table/Pagination";
import { atom, useRecoilState } from "recoil";
import Table, { TableBody, TableBodyItem } from "../../components/Table/Table";
import { useApi } from "../../hooks/useApi";
import ConfirmBtn from "../../components/Button/ConfirmBtn";
import Loader from "../../components/DefaultStyles/Loader";
import SpaceBetween from "../../components/DefaultStyles/SpaceBetween";
import Flex from "../../components/DefaultStyles/Flex";
import DefaultModalLayout from "../../components/Modal/DefaultModalLayout";
import { Input } from "../../components/Input/Input";
import { toast } from "react-toastify";
import axios from "axios";

interface ITimeEvent {
  readonly id: number;
  readonly eventName: string;
  readonly code: string;
  readonly startDate: string;
  readonly startTime: string;
  readonly endDate: string;
  readonly endTime: string;
  readonly state: number;
}

interface ITimeEventForm {
  id?: number;
  eventName: string;
  code: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  state: number;
}

const INITIAL_FORM: ITimeEventForm = {
  eventName: "",
  code: "",
  startDate: "",
  startTime: "",
  endDate: "",
  endTime: "",
  state: 0,
};

export const timeEventPageState = atom<number>({
  key: "timeEventPageState",
  default: 0,
});

const normalizeDateTimeStr = (dateStr: string): string => {
  if (!dateStr) return "";
  const s = dateStr.trim();
  let date = "";
  let time = "00:00";

  // Try to extract date and possibly time from the string
  // "2026-04-17T01:23" or "2026-04-17 01:23:00"
  const dtMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (dtMatch) {
    date = `${dtMatch[1]}-${dtMatch[2]}-${dtMatch[3]}`;
    time = `${dtMatch[4]}:${dtMatch[5]}`;
    return `${date}T${time}`;
  }

  const dashMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dashMatch) date = `${dashMatch[1]}-${dashMatch[2]}-${dashMatch[3]}`;
  if (!date) {
    const dotSlashMatch = s.match(/^(\d{4})[./](\d{2})[./](\d{2})/);
    if (dotSlashMatch) date = `${dotSlashMatch[1]}-${dotSlashMatch[2]}-${dotSlashMatch[3]}`;
  }
  if (!date) {
    const compactMatch = s.match(/^(\d{4})(\d{2})(\d{2})/);
    if (compactMatch) date = `${compactMatch[1]}-${compactMatch[2]}-${compactMatch[3]}`;
  }
  if (!date) date = s;

  return `${date}T${time}`;
};

const normalizeTimeStr = (str: string): string => {
  if (!str) return "";
  const t = str.trim();
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(t)) return t.slice(0, 5);
  if (/^\d{4}$/.test(t)) return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
  if (/^\d{6}$/.test(t)) return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
  return t;
};

const splitDateTime = (dt: string): { date: string; time: string } => {
  if (!dt) return { date: "", time: "" };
  const [date, time] = dt.split("T");
  return { date: date || "", time: time || "" };
};

export default function TimeEvent() {
  const [savedPage, setSavedPage] = useRecoilState<number>(timeEventPageState);
  const [page, setPage] = useState(savedPage);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<ITimeEventForm>({ ...INITIAL_FORM });
  const [submitting, setSubmitting] = useState(false);

  const isFormValid =
    form.eventName.trim() !== "" &&
    form.code.trim() !== "" &&
    form.startDate !== "" &&
    form.startTime !== "" &&
    form.endDate !== "" &&
    form.endTime !== "";

  useEffect(() => {
    setSavedPage(page);
  }, [page]);

  const { response, loading, fetchData } = useApi({
    method: "get",
    url: `benefit/eventlist?page=${page}&size=10`,
  });

  useEffect(() => {
    fetchData({
      method: "get",
      url: `benefit/eventlist?page=${page}&size=10`,
    });
  }, [page]);

  const tableHead = [
    "ID",
    "이벤트명",
    "코드",
    "시작일",
    "시작시간",
    "종료일",
    "종료시간",
    "상태",
    "설정",
  ];

  const getStateLabel = (state: number) => {
    switch (state) {
      case 0:
        return "비활성";
      case 1:
        return "활성";
      default:
        return String(state);
    }
  };

  const isExpired = (item: ITimeEvent): boolean => {
    try {
      const endDateTime = normalizeDateTimeStr(item.endDate);
      return new Date(endDateTime) < new Date();
    } catch {
      return false;
    }
  };

  const handleOpenNewModal = () => {
    setForm({ ...INITIAL_FORM });
    setModalOpen(true);
  };

  const handleOpenEditModal = (item: ITimeEvent) => {
    setForm({
      id: item.id,
      eventName: item.eventName,
      code: item.code,
      startDate: normalizeDateTimeStr(item.startDate),
      startTime: normalizeTimeStr(item.startTime),
      endDate: normalizeDateTimeStr(item.endDate),
      endTime: normalizeTimeStr(item.endTime),
      state: item.state,
    });
    setModalOpen(true);
  };

  const handleFormChange = (field: keyof ITimeEventForm, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.eventName.trim()) {
      toast.error("이벤트명을 입력해주세요.");
      return;
    }
    if (!form.code.trim()) {
      toast.error("코드를 입력해주세요.");
      return;
    }
    if (!form.startDate || !form.startTime || !form.endDate || !form.endTime) {
      toast.error("시작/종료 일시를 모두 입력해주세요.");
      return;
    }

    setSubmitting(true);
    try {
      const isEdit = form.id !== undefined && form.id !== 0;
      const startDt = splitDateTime(form.startDate);
      const endDt = splitDateTime(form.endDate);
      const payload = {
        id: isEdit ? form.id! : 0,
        eventName: form.eventName,
        code: form.code,
        startDate: form.startDate,
        startTime: form.startTime,
        endDate: form.endDate,
        endTime: form.endTime,
        state: form.state,
      };

      const result = await axios.post(`/api/benefit/eventupdate`, payload);
      if (result?.status === 200) {
        toast.success(isEdit ? "수정되었습니다." : "등록되었습니다.");
        setModalOpen(false);
        fetchData({
          method: "get",
          url: `benefit/eventlist?page=${page}&size=10`,
        });
      }
    } catch (err) {
      toast.error("처리 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageTitle title="타임이벤트 관리" />

      <SpaceBetween style={{ marginBottom: 10 }}>
        <TotalCountText>
          총 {response?.data?.totalCount ?? 0}건
        </TotalCountText>
        <ConfirmBtn
          btnText="신규 등록"
          btnColor="mint"
          btnClickEvent={handleOpenNewModal}
        />
      </SpaceBetween>

      <LineBox>
        <Table tableHeadData={tableHead}>
          {response?.data?.items?.map((item: ITimeEvent) => {
            const expired = isExpired(item);
            const rowStyle = expired ? { color: "#aaaaaa", backgroundColor: "#f6f6f8" } : undefined;
            return (
            <TableBody key={item.id} style={rowStyle}>
              <TableBodyItem>{item.id}</TableBodyItem>
              <TableBodyItem>{item.eventName}</TableBodyItem>
              <TableBodyItem>{item.code}</TableBodyItem>
              <TableBodyItem>{item.startDate}</TableBodyItem>
              <TableBodyItem>{item.startTime}</TableBodyItem>
              <TableBodyItem>{item.endDate}</TableBodyItem>
              <TableBodyItem>{item.endTime}</TableBodyItem>
              <TableBodyItem>
                <StateLabel state={expired ? -1 : item.state}>
                  {expired ? "만료" : getStateLabel(item.state)}
                </StateLabel>
              </TableBodyItem>
              <TableBodyItem>
                <ConfirmBtn
                  btnText="설정"
                  btnColor="mint"
                  btnClickEvent={() => handleOpenEditModal(item)}
                />
              </TableBodyItem>
            </TableBody>
            );
          })}
        </Table>
        <Pagination
          totalPage={response?.data?.totalPage ?? 0}
          currentPage={page}
          pageClickEvent={(i: number) => setPage(i)}
        />
      </LineBox>

      {modalOpen && (
        <DefaultModalLayout
          modalTitle={form.id !== undefined ? "타임이벤트 수정" : "타임이벤트 등록"}
          type={form.id !== undefined ? "edit" : "input"}
          confirmEvent={handleSubmit}
          closeEvent={() => setModalOpen(false)}
          confirmBtnText={form.id !== undefined ? "수정" : "등록"}
          cancelBtnText="닫기"
          confirmDisabled={!isFormValid}
          isMediumModal
        >
          <ModalContent>
            <FormRow>
              <FormLabel>이벤트명</FormLabel>
              <Input
                value={form.eventName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFormChange("eventName", e.target.value)
                }
                placeholder="이벤트명 입력"
              />
            </FormRow>
            <FormRow>
              <FormLabel>코드</FormLabel>
              <Input
                value={form.code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFormChange("code", e.target.value)
                }
                placeholder="코드 입력"
              />
            </FormRow>
            <FormRow>
              <FormLabel>시작일시</FormLabel>
              <Input
                type="datetime-local"
                value={form.startDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFormChange("startDate", e.target.value)
                }
              />
            </FormRow>
            <FormRow>
              <FormLabel>시작시간</FormLabel>
              <Input
                type="time"
                value={form.startTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFormChange("startTime", e.target.value)
                }
              />
            </FormRow>
            <FormRow>
              <FormLabel>종료일시</FormLabel>
              <Input
                type="datetime-local"
                value={form.endDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFormChange("endDate", e.target.value)
                }
              />
            </FormRow>
            <FormRow>
              <FormLabel>종료시간</FormLabel>
              <Input
                type="time"
                value={form.endTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFormChange("endTime", e.target.value)
                }
              />
            </FormRow>
            <FormRow>
              <FormLabel>상태</FormLabel>
              <Flex gapSize={8}>
                <StateButton
                  active={form.state === 1}
                  onClick={() => handleFormChange("state", 1)}
                >
                  활성
                </StateButton>
                <StateButton
                  active={form.state === 0}
                  onClick={() => handleFormChange("state", 0)}
                >
                  비활성
                </StateButton>
              </Flex>
            </FormRow>
          </ModalContent>
        </DefaultModalLayout>
      )}

      {(loading || submitting) && <Loader />}
    </>
  );
}

const TotalCountText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.color.textColor};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 10px 0;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.textColor};
`;

const StateLabel = styled.span<{ state: number }>`
  color: ${({ state }) => (state === 1 ? "#00c7c7" : state === -1 ? "#aaaaaa" : "#f42d16")};
  font-weight: 600;
`;

const StateButton = styled.button<{ active: boolean }>`
  padding: 6px 16px;
  border-radius: 5px;
  border: 1px solid
    ${({ active }) => (active ? "#00c7c7" : "#d4d9e1")};
  background-color: ${({ active }) => (active ? "#00c7c7" : "#fff")};
  color: ${({ active }) => (active ? "#fff" : "#2f2f38")};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #00c7c7;
  }
`;
